import LogRocket from 'logrocket';
import {
  snakeCase,
  isPlainObject,
  isString,
} from 'lodash';
import { Middleware } from './types';

const encodeValue = (item: Record<string, any> | string): string => {
  if (isPlainObject(item)) {
    return encodeURIComponent(JSON.stringify(item));
  }

  if (isString(item)) {
    return encodeURIComponent(item);
  }

  return '';
};

const asFormData = (data: Record<string, any>): FormData => {
  if (data instanceof FormData) {
    return data;
  }

  const formData = new FormData();
  Object.keys(data).forEach((key) => {
    const value = isPlainObject(data[key]) || Array.isArray(data[key])
      ? JSON.stringify(data[key])
      : data[key];

    formData.append(key, value);
  });

  return formData;
};

// This is a shallow serializer so we will eventually have to upgrade it so
// that we can deep copy it
const serialize = (params: Record<string, string>): string => {
  if (!params) {
    return '';
  }

  return Object.entries(params)
    .map(([key, value]) => `${encodeValue(key)}=${encodeValue(value)}`)
    .join('&');
};

const toSnakeCase = (data: Record<string, any> = {}): Record<string, any> => {
  if (isString(data)) {
    return data;
  }

  return Object.keys(data).reduce((acc, key) => ({
    ...acc,
    [snakeCase(key)]: data[key],
  }), {});
};

export const serializeParams: Middleware = next => (reqData) => {
  if (reqData.method !== 'GET') {
    return next(reqData);
  }

  const serializedParams = serialize(reqData.data);

  let fullPath = reqData.path;

  if (serializedParams.length > 0) {
    fullPath = fullPath.concat(`?${serializedParams}`);
  }

  return next({
    ...reqData,
    path: fullPath,
    data: undefined,
  });
};

export const logToLogRocket: Middleware = next => ({ options, ...rest }) => {
  LogRocket.info('REQUEST INFO:', rest);

  return next({ ...rest, options });
};

export const wrapError: Middleware = next => reqData => {
  try {
    return next(reqData);
  } catch (e) {
    throw new Error(e);
  }
};

export const extractData: Middleware = next => async (reqData) => {
  const res = await (next(reqData) as Response);

  const dataAsText = await res.text();

  const finalizedData = await ((): string | Record<string, any> => {
    try {
      return JSON.parse(dataAsText);
    } catch {
      return dataAsText;
    }
  })();

  // Monkeypatch the response with data
  res.data = finalizedData;

  return res;
};

export const determineError: Middleware = next => async (reqData) => {
  const res = await (next(reqData) as Response);

  if (res.ok) {
    return res;
  }

  return Promise.reject(res);
};

export const serializeData: Middleware = next => (reqData) => next({
  ...reqData,
  data: serialize(reqData.data),
});

export const snakeifyData: Middleware = next => (reqData) => next({
  ...reqData,
  data: toSnakeCase(reqData.data),
});

export const convertDataToFormData: Middleware = next => (reqData) => next({
  ...reqData,
  data: asFormData(reqData.data),
});

export const logger: Middleware = next => (reqData) => {
  /* eslint-disable */
  console.groupCollapsed(reqData.path)
  console.dir(reqData);
  console.groupEnd()
  /* eslint-enable */

  return next(reqData);
};
